import React, { Component } from 'react';
import { Route } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { FormattedMessage } from 'react-intl';
import Constants from '../../../definitions/constants';
import InterfaceTooling from '../../../tooling/interfaceTooling';
import JavascriptUtilsTooling from '../../../tooling/javascriptUtilsTooling';
import FormatTooling from '../../../tooling/formatTooling';
import EntityTableTooling from '../../../tooling/entityTableTooling';
import TableDescriptors from '../../../definitions/tableDescriptors';
import LocaleTooling from '../../../tooling/localeTooling';
import { intl } from '../../../locales/localesConfig';
import toastr, { version } from 'toastr';

/* Actions */
import * as periodAdminActions from '../../actions/periodAdmin/index';
import * as dashboardActions from '../../actions/dashboard/index';
import * as reportFileActions from '../../actions/reportFileAdmin/index';

/*Components*/
import FullScreenGlassLoading from '../../components/common/interface/fullScreenGlassLoading';
import GlassConfirmPopup from '../../components/common/interface/glassConfirmPopup';
import Header from '../../components/periodAdmin/header';
import CalculationsHeader from '../../components/periodAdmin/calculationsHeader';
import CalculationsUnitsHeader from '../../components/periodAdmin/calculationsUnitsHeader';
import FormPopup from '../../components/periodAdmin/formPopup';
import NoPeriodCreated from '../../components/periodAdmin/noPeriodCreated';
import PeriodInfo from '../../components/periodAdmin/periodInfo';
import CalculationUnitsFilter from '../../components/periodAdmin/calculationUnitsFilter';
import CalculateButton from '../../components/periodAdmin/calculateButton';
import ChangePeriodStateButton from '../../components/periodAdmin/changePeriodStateButton';
import ReprocessForm from '../../components/periodAdmin/reprocessForm';
import CalculateForm from '../../components/periodAdmin/calculateForm';
import ClosePeriodButton from '../../components/periodAdmin/closePeriodButton';
import LoadDataHeader from '../../components/periodAdmin/loadDataHeader';
import CalculationsUnitsLoadData from '../../components/periodAdmin/calculationsUnitsLoadData';
import ImportMetricDataFormPopup from '../../components/periodAdmin/importMetricDataFormPopup';
import CalculationsPopup from '../../components/periodAdmin/calculationsPopup';
import LoadDataInfoPopup from '../../components/periodAdmin/loadDataInfoPopup';
import ReportFilesPopupContainer from '../reportFileAdmin/reportFilesPopupContainer';
import ExportBiReportsFormPopup from '../../components/periodAdmin/exportBiReportsFormPopup';
import Select from '../../components/common/inputs/select';
import EntityHeader from '../../components/common/entity/entityHeader';
import MetricGroupsLoadData from '../../components/periodAdmin/metricGroupsLoadData';
import ErrorLoadingView from '../../components/common/interface/errorLoadingView';

const popupIds = {
  formPopup: "formPopup",
  calculationsPopup: "calculationsPopup",
  calculationDeletePopup: "calculationDeletePopup",
  calculateConfirmPopup: "calculateConfirmPopup",
  recalculateConfirmPopup: "recalculateConfirmPopup",
  changePeriodStateConfirmPopup: "changePeriodStateConfirmPopup",
  rollBackPeriodConfirmPopup: "rollBackPeriodConfirmPopup",
  closePeriodConfirmPopup: "closePeriodConfirmPopup",
  loadDataInfoPopup: "loadDataInfoPopup",
  reportFilesPopup: "reportFilesPopup",
  exportBiReportsPopup: "exportBiReportsPopup",
  closeFormConfirmPopup: "closeFormConfirmPopup",
}

const initialState = {
  entityApiKey: "period",
  initialNewEntity: { start_date: "", end_date: "", query_date: "", fix_polynomials_date: "" },
  calculationsCollapse: false,
  calculationFilters: {
    filterGroup: "",
    criteria: Constants.calculationFilters.calculationUnitFilter.ALL,
    calculationUnitsSelected: [],
    filterPolynomial: "",
    filterPolynomialSubModel: ""
  },
  reprocessForm: {
    default_reprocess_tag: "",
    default_reprocess_file_string: "",
    fix_polynomials: true,
    fix_polynomials_date: "",
  },
  isDebugCalculateForm: false,
  calculateForm: {
    debug_mode: Constants.selectKeys.periodAdmin.calculationDebugModeTypes.NO_DEBUG,
  },
  metricsIdsSelected: [],
  metricGroupSelected: null,
  selectedImportFile: null,
  recalculationFilters: null,
  deleteCalculationTextInfo: "",
  exportBiReportsForm: {
    models_list: [],
  },
  connectorSelected: "",
  metricUploadFilters: {
    selectedPolynomialIdToSend: "",
    selectedPolynomialIdModel: "",
    selectedPolynomialIdSubModel: ""
  },  
  formHasChanges: false,
};

class PeriodAdmin extends Component {
  constructor(props) {
    super(props);
    this.state = JavascriptUtilsTooling.deepCopy(initialState);
  }
 
  async componentDidMount() {
    await this.props.actions.getMetricsGroupsWithDefaults();
    await this.props.actions.getPeriodsList();
    await this.props.actions.getPolynomialsList();
    await this.props.actions.getPolynomialsSubModelList();    
    await this.props.actions.getImportFilesList();
    await this.props.actions.getCustomer();
  }
  
  componentWillUnmount() {
    this.props.actions.reset();
  }
  
  async componentDidUpdate(prevProps) {
    //handle save feedback
    InterfaceTooling.apiCallResultFeedback(
      [prevProps.save.status],
      [this.props.save.status],
      intl.formatMessage( {id: "periodAdmin.createPeriodSuccess"}),
      null,
      [() => {
        if(InterfaceTooling.popupHasToRender(this.state[popupIds.formPopup])) {
          InterfaceTooling.popupHide(popupIds.formPopup, this.setState.bind(this));
        }
        this.props.actions.getPeriodsList();      
        this.setState({ formHasChanges: false });     
      }]
    );
    
    //handle download imoprt feedback
    InterfaceTooling.apiCallResultFeedback(
      [prevProps.downloadImport.status],
      [this.props.downloadImport.status],
      intl.formatMessage({ id: "common.entity.downloadImportFileSuccess" }),
      intl.formatMessage({ id: "common.entity.downloadImportFileFailure" })
    );

    //handle imoprt feedback
    InterfaceTooling.apiCallResultFeedback(
      [prevProps.import.status],
      [this.props.import.status],
      intl.formatMessage(
        { id: "common.entity.uploadImportSuccess" },
        {entityNameSingular: intl.formatMessage({ id: "metricDataAdmin.entityNamePlural" })}),
      null,
      [() => {
        if(InterfaceTooling.popupHasToRender(this.state[popupIds.importFormPopup])) {
          InterfaceTooling.popupHide(popupIds.importFormPopup, this.setState.bind(this));
        }    
      }]  
    );
    
    //handle calculation feedback
    InterfaceTooling.apiCallResultFeedback(
      [prevProps.calculate.status],
      [this.props.calculate.status],
      intl.formatMessage({ id: "periodAdmin.calculateSuccess" }),
      this.props.calculate.status==423 ?
        intl.formatMessage({ id: "common.errors.systemLockedError" }) :
        intl.formatMessage({ id: "periodAdmin.calculateFailure" }),
      [async () => {
        if(InterfaceTooling.popupHasToRender(this.state[popupIds.calculateConfirmPopup])) {
          InterfaceTooling.popupHide(popupIds.calculateConfirmPopup, this.setState.bind(this));
        }
        if(InterfaceTooling.popupHasToRender(this.state[popupIds.recalculateConfirmPopup])) {
          InterfaceTooling.popupHide(popupIds.recalculateConfirmPopup, this.setState.bind(this));
        }
      }]
    );
    
    //handle change period state feedback
    InterfaceTooling.apiCallResultFeedback(
      [prevProps.changePeriodState.status],
      [this.props.changePeriodState.status],
      intl.formatMessage({ id: "periodAdmin.changePeriodStateSuccess" }),
      this.props.changePeriodState.status==423 ?
        intl.formatMessage({ id: "common.errors.systemLockedError" }) :
        (this.props.changePeriodState.status==422 ? intl.formatMessage({ id: "periodAdmin.changePeriodStateDateFailure" }) : intl.formatMessage({ id: "periodAdmin.changePeriodStateFailure" })),
      [async () => {
        if(InterfaceTooling.popupHasToRender(this.state[popupIds.changePeriodStateConfirmPopup])) {
          InterfaceTooling.popupHide(popupIds.changePeriodStateConfirmPopup, this.setState.bind(this));
        }
        this.props.actions.getPeriodsList();
      }]
    );
    
    //handle close period feedback
    InterfaceTooling.apiCallResultFeedback(
      [prevProps.closePeriod.status],
      [this.props.closePeriod.status],
      intl.formatMessage({ id: "periodAdmin.closePeriodSuccess" }),
      this.props.closePeriod.status==423 ?
        intl.formatMessage({ id: "common.errors.systemLockedError" }) :
        intl.formatMessage({ id: "periodAdmin.closePeriodFailure" }),
      [async () => {
        if(InterfaceTooling.popupHasToRender(this.state[popupIds.closePeriodConfirmPopup])) {
          InterfaceTooling.popupHide(popupIds.closePeriodConfirmPopup, this.setState.bind(this));
        }
      }]
    );

    //handle rollback period feedback
    InterfaceTooling.apiCallResultFeedback(
      [prevProps.rollBackPeriod.status],
      [this.props.rollBackPeriod.status],
      intl.formatMessage({ id: "periodAdmin.rollBackPeriodSuccess" }),
      this.props.rollBackPeriod.status==406 ?
        intl.formatMessage({id: "periodAdmin.changePeriodStateCantRollbackReprocessFailure"}) :
      this.props.rollBackPeriod.status==423 ?
        intl.formatMessage({ id: "common.errors.systemLockedError" }) :
        intl.formatMessage({ id: "periodAdmin.rollBackPeriodFailure" }),
      [async () => {
        if(InterfaceTooling.popupHasToRender(this.state[popupIds.rollBackPeriodConfirmPopup])) {
          InterfaceTooling.popupHide(popupIds.rollBackPeriodConfirmPopup, this.setState.bind(this));
        }
      }]
    );
    
    //handle delete calculation feedback
    InterfaceTooling.apiCallResultFeedback(
      [prevProps.deleteCalculation.status],
      [this.props.deleteCalculation.status],
      intl.formatMessage({ id: "periodAdmin.deleteCalculationSuccess" }),
      this.props.deleteCalculation.status==423 ?
        intl.formatMessage({ id: "common.errors.systemLockedError" }) :
        intl.formatMessage(
          { id: "common.entity.deleteEntityError" },
          {entityNameSingular: TableDescriptors.calculationsList.entityNameSingularWithArticle.toLowerCase()}
      ),
      [() => {
        if(InterfaceTooling.popupHasToRender(this.state[popupIds.calculationDeletePopup])) {
          InterfaceTooling.popupHide(popupIds.calculationDeletePopup, this.setState.bind(this));
        }
        this.props.actions.getCalculationsHistory({
          period_id: this.props.findActive.data.id,
          per_page: Constants.interface.popupPageSize,
          page: this.props.calculationsHistory.page ? this.props.calculationsHistory.page : 1
        }); 
      }]    
    );
    
    //handle download calculation report feedback
    InterfaceTooling.apiCallResultFeedback(
      [prevProps.downloadCalculationReport.status],
      [this.props.downloadCalculationReport.status],
      intl.formatMessage({ id: "calculations.downloadCalculationReportFileSuccess" }),
      intl.formatMessage({ id: "calculations.downloadCalculationReportFileFailure" })
    );
    
    //handle download calculation report feedback
    InterfaceTooling.apiCallResultFeedback(
      [prevProps.downloadCalculationUnifiedReport.status],
      [this.props.downloadCalculationUnifiedReport.status],
      intl.formatMessage({ id: "calculations.downloadCalculationReportFileSuccess" }),
      intl.formatMessage({ id: "calculations.downloadCalculationReportFileFailure" })
    );
    
    //handle error on loading metrics data upload info
    InterfaceTooling.apiCallResultFeedback(
      [prevProps.metricsDataUploadInfo.status],
      [this.props.metricsDataUploadInfo.status],
      null,
      intl.formatMessage({ id: "periodAdmin.getMetricsDataUploadInfoFailure" })
    );    
    
    //handle export bi reports feedback
    InterfaceTooling.apiCallResultFeedback(
      [prevProps.exportBiReports.status],
      [this.props.exportBiReports.status],
      intl.formatMessage({ id: "periodAdmin.exportBiReportsSuccess" }),
      null,
      [() => {
        if(InterfaceTooling.popupHasToRender(this.state[popupIds.exportBiReportsPopup])) {
          InterfaceTooling.popupHide(popupIds.exportBiReportsPopup, this.setState.bind(this));
        }    
      }]  
    );
    
    //handle refresh virtual metrics feedback
    InterfaceTooling.apiCallResultFeedback(
      [prevProps.refreshVirtualMetricData.status],
      [this.props.refreshVirtualMetricData.status],
      intl.formatMessage( {id: "periodAdmin.refreshVirtualMetricDataCreateJobSuccess"}),
      this.props.refreshVirtualMetricData.status==423 ?
        intl.formatMessage({ id: "common.errors.systemLockedError" }) :
        intl.formatMessage({ id: "periodAdmin.refreshVirtualMetricDataCreateJobFailure" })
    );
    
    if (((this.props.periodsList.status===200 && prevProps.periodsList.status !==200) ||
        (this.props.metricsGroupsWithDefaults.status===200 && prevProps.metricsGroupsWithDefaults.status !==200)) &&
        (this.props.periodsList.status===200 && this.props.metricsGroupsWithDefaults.status===200)) {
      if(this.props.periodsList.data.length > 0) {
        if(this.props.findActive.data.id) {
          let selectedPeriod = this.props.periodsList.data.find(item => item.id==this.props.findActive.data.id);
          selectedPeriod = selectedPeriod ? selectedPeriod : {}
          this.props.actions.setActivePeriod(selectedPeriod);
          this.getMetricsDataUploadInfoForAllMetrics(selectedPeriod.id);
        }
        else {
          let selectedPeriod = this.props.periodsList.data[0];
          let periodIdSelected = selectedPeriod.id;
          if(this.props.match.params.periodId) {
            selectedPeriod = this.props.periodsList.data.find(item => item.id==this.props.match.params.periodId);
            periodIdSelected = selectedPeriod ? selectedPeriod.id : null;
          }
          if(this.props.fixedPeriodId!="") {
            selectedPeriod = this.props.periodsList.data.find(item => item.id==this.props.fixedPeriodId);
            periodIdSelected = selectedPeriod ? selectedPeriod.id : null;
          }
          this.props.actions.setActivePeriod(selectedPeriod);
          await this.props.actions.getGroupsList(periodIdSelected);
          await this.props.actions.getBiTemplatesList(periodIdSelected);
          this.getMetricsDataUploadInfoForAllMetrics(periodIdSelected);
        }
      }
    }
  }
  
  getMetricsDataUploadInfoForAllMetrics = (periodIdSelected) => {
    this.props.metricsGroupsWithDefaults.data.forEach((item, index) => {
      this.props.actions.getMetricsDataUploadInfo({
        metric_version_ids: [item.metric_version_ids ? item.metric_version_ids.split(',') : []],
        period_id: periodIdSelected,
      }, index);
    });
  };
  
  handleCalculationsCollapse = (event) => {
    this.setState({
      calculationsCollapse: !this.state.calculationsCollapse
    });
  };

  handleOnChangePeriod = async (event) => {
    let value = event.target.value;
    let newState = JavascriptUtilsTooling.deepCopy(initialState);
    let selectedPeriod = this.props.periodsList.data.find(item => item.id==value);
    let selectedPeriodId = selectedPeriod ? selectedPeriod.id : null;
    this.setState(newState);
    this.props.actions.softReset();
    this.props.actions.setActivePeriod(selectedPeriod);
    await this.props.actions.getMetricsGroupsWithDefaults();
    await this.props.actions.getGroupsList(selectedPeriodId);
    await this.props.actions.getBiTemplatesList(selectedPeriodId);
    await this.props.actions.getCustomer();
    await this.props.actions.getPeriodsList();
  };

  handleOnChangeCalculationFilters = (event) => {
    let path = event.currentTarget.dataset.path;
    let value = this.state.calculationFilters[path];
    let calculationFiltersNew = JavascriptUtilsTooling.deepCopy(this.state.calculationFilters);
    
    if(event.currentTarget.dataset.selectlist) {
      let index = event.currentTarget.dataset.index;
      let listValue = event.currentTarget.value;
      let isDelete = event.currentTarget.dataset.delete;
      
      let calculation_units = calculationFiltersNew[path]
            
      if(isDelete) { 
        delete calculation_units[index];
        calculation_units = calculation_units.filter(item => item);
      } else {
        var selectedItem = this.props.calculationUnitsFilter.data.find(item => item.id==listValue);
        var exists = this.state.calculationFilters.calculationUnitsSelected.find(item => item.id==listValue);
        if(selectedItem && !exists) {
          calculation_units.push({id: listValue, name: selectedItem.name });
        }
        else if(exists) {
          toastr.options = Constants.interface.tostrOptions;
          toastr.warning(LocaleTooling.getCalculationUnitContextLocale(intl.formatMessage( {id: "periodAdmin.calculationUnitAlreadyAdded"})),""); 
        }
      }
      value = calculation_units;
    }
    else {
      value = JavascriptUtilsTooling.getInputValueFromEvent(event);
    }
    calculationFiltersNew[path] = value;
    
    if((path=="criteria" && value==Constants.calculationFilters.calculationUnitFilter.ALL) ||
      (path=="filterGroup" && value!="" && value!=this.state.calculationFilters.filterGroup)) {
      calculationFiltersNew.calculationUnitsSelected = [];
    }
    else if(path=="filterPolynomial" && value!="") {
      calculationFiltersNew.filterPolynomialSubModel = "";
    }
    else if(path=="filterPolynomialSubModel" && value!="") {
      calculationFiltersNew.filterPolynomial = "";
    }
    
    this.setState({
      calculationFilters: calculationFiltersNew
    });    
  };

  handleFetchFilterCalculationUnits = async (keyword) => {
    await this.props.actions.getCalculationUnitsList(keyword, this.state.calculationFilters.filterGroup, this.props.findActive.data.id);
  };
  
  handleClickMenuItem = async event => {
    var itemRoute = event.target.dataset.route;
    this.props.dashboardActions.setActiveMainMenu(itemRoute);
  };

  handleShowImportFormPopup = (event) => {
    InterfaceTooling.handleShowPopup(
      event, popupIds.importFormPopup, this.state, 
      this.setState.bind(this), this.props.import.loading, () => {
        var metricsGroup = JSON.parse(event.currentTarget.dataset.entitydata);
        this.props.actions.resetImport();
        this.setState({
          metricsIdsSelected: metricsGroup.metric_ids ? metricsGroup.metric_ids.split(",") : [],
          metricGroupSelected: metricsGroup,
          selectedImportFile: null
        });
      }    
    );
  };

  handleDownloadImportSheet = async () => {
    await this.props.actions.downloadImportFile({
      group_name: this.state.metricGroupSelected.name ? this.state.metricGroupSelected.name : "",
      period_id: this.props.findActive.data.id,
      metrics_ids: this.state.metricsIdsSelected
    });
  };

  handlePolynomialFilterSelection = (event) => {
    let newSelectedPolynomialId = event.currentTarget.value;
    let metricUploadFiltersPath = event.currentTarget.dataset.path;
    let newMetricUploadFilters = JavascriptUtilsTooling.deepCopy(this.state.metricUploadFilters)
    newMetricUploadFilters.selectedPolynomialIdToSend = newSelectedPolynomialId;
    newMetricUploadFilters.selectedPolynomialIdModel = "";
    newMetricUploadFilters.selectedPolynomialIdSubModel = "";
    newMetricUploadFilters[metricUploadFiltersPath] = newSelectedPolynomialId
    this.setState({
      metricUploadFilters: newMetricUploadFilters
    });
    if(newSelectedPolynomialId && newSelectedPolynomialId!=Constants.selectKeys.periodAdmin.allModelsSelectKey) {
      this.props.actions.getPolynomialFilterTreeInfo({
        polynomial_id: newSelectedPolynomialId,
        period_id: this.props.findActive.data.id
      });
    }
  }

  handleOnSaveImportForm = async (event) => {
    let files = [];
    if(!this.state.selectedImportFile) {
      files = document.getElementById('import_file').files;
      files = Object.keys(files).map(key => files[key]);
    }
    document.getElementById('import_file').value = '';
    await this.props.actions.importEntities(files,{
      period_id: this.props.findActive.data.id,
      metrics_ids: this.state.metricsIdsSelected,
      group_name: this.state.metricGroupSelected.name ? this.state.metricGroupSelected.name : "", 
      import_file_url: this.state.selectedImportFile
    });
  };

  handleOnChangeImportFile = (event) => {
    let value = JavascriptUtilsTooling.getInputValueFromEvent(event);
    this.setState({
      selectedImportFile: value
    });
  };

  handleCalculationsPopup = event => {
    InterfaceTooling.handleShowPopup(
      event, popupIds.calculationsPopup, this.state, 
      this.setState.bind(this), this.props.calculationsHistory.loading, () => {
        if(this.state.connectorSelected=="") {
          this.setState({
            connectorSelected: this.props.getCustomer.data.connectors.length>0 ? this.props.getCustomer.data.connectors[0] : ""
          });
        }
        this.props.actions.getCalculationsHistory({
          period_id: this.props.findActive.data.id,
          per_page: Constants.interface.popupPageSize,
          page: 1
        });
      }
    );
  };

  handleRefreshCalculations = event => { 
    this.props.actions.getCalculationsHistory({
      period_id: this.props.findActive.data.id,
      per_page: Constants.interface.popupPageSize,
      page: this.props.calculationsHistory.page ? this.props.calculationsHistory.page : 1
    });
  };

  handleCalculationsPopupNextPage = async (event) => {
    var page = +event.currentTarget.dataset.targetpage;
    if(this.props.calculationsHistory.status === 200 && this.props.calculationsHistory.page<=this.props.calculationsHistory.totalPages) {
      this.props.actions.getCalculationsHistory({
        period_id: this.props.findActive.data.id,
        per_page: Constants.interface.popupPageSize,
        page: page
      });
    }  
  };

  handleCalculationDeletePopup = async (event) => {
    InterfaceTooling.handleShowPopup(
      event, popupIds.calculationDeletePopup, this.state, 
      this.setState.bind(this), this.props.deleteCalculation.loading, () => {
        var calculation = JSON.parse(event.currentTarget.dataset.entitydata);
        this.props.actions.prepareDeleteCalculation(event.currentTarget.dataset.id);
        this.setState({
          deleteCalculationTextInfo: FormatTooling.toUpperCaseFirst(intl.formatMessage( {id: "calculations.entityNameSingular"})) + 
            ": " + FormatTooling.defaultsTextFormatTypes(calculation.created_at,Constants.crudGenerator.dataTypes.DATETIME) + 
            " - " + calculation.detail
        });
      }
    );
  };
    
  handleCalculationDeleteConfirm = async () => {
    await this.props.actions.deleteCalculation({
      id: this.props.deleteCalculation.id,
      calculation_detail_text: this.state.deleteCalculationTextInfo
    });
  };

  notCalculationUnitsDownloadError = (calculation) => {
    if(!calculation.calculation_units_count) {
      toastr.options = Constants.interface.tostrOptions;
      toastr.error(LocaleTooling.getCalculationUnitContextLocale(intl.formatMessage({id: "periodAdmin.downloadNotCalculationUnitsError"})),"");
      return true;
    }
    return false;
  }

  handleDownloadCalculationReport = async (event) => {
    var calculation = JSON.parse(event.currentTarget.dataset.entitydata);
    if(this.notCalculationUnitsDownloadError(calculation)) return;
    await this.props.actions.downloadCalculationReportFile({id: calculation.id});
  };

  handleDownloadCalculationReportCustom = async (event) => {
    var calculation = JSON.parse(event.currentTarget.dataset.entitydata);
    if(this.notCalculationUnitsDownloadError(calculation)) return;
    await this.props.actions.downloadCalculationReportFile({
      id: calculation.id,
      report_type: this.state.connectorSelected,
    });
  };

  handleDownloadCalculationUnifiedReport = async (event) => {    
    await this.props.actions.downloadCalculationUnifiedReportFile({period_id: this.props.findActive.data.id});
  };

  handleDownloadCalculationUnifiedReportCustom = async (event) => {
    await this.props.actions.downloadCalculationUnifiedReportFile({
      period_id: this.props.findActive.data.id,
      report_type: this.state.connectorSelected,
    });
  };

  handleDownloadCalculationUnifiedReportReprocess = async (event) => {    
    await this.props.actions.downloadCalculationUnifiedReportFile({
      period_id: this.props.findActive.data.id,
      reprocess: true
    });
  };

  handleDownloadCalculationUnifiedReportCustomReprocess = async (event) => {
    await this.props.actions.downloadCalculationUnifiedReportFile({
      period_id: this.props.findActive.data.id,
      report_type: this.state.connectorSelected,
      reprocess: true
    });
  };

  handleCalculateConfirmPopup = async (event) => {
    let isDebug = event.currentTarget.dataset.debug=="true";
    InterfaceTooling.handleShowPopup(
      event, popupIds.calculateConfirmPopup, this.state, 
      this.setState.bind(this), this.props.calculate.loading, () => {
        let newCalculateForm = JavascriptUtilsTooling.deepCopy(initialState.calculateForm);
        if(isDebug) {
          newCalculateForm.debug_mode = Constants.selectKeys.periodAdmin.calculationDebugModeTypes.NORMAL_DEBUG;
        }
        this.setState({
          isDebugCalculateForm: isDebug,
          calculateForm: newCalculateForm
        });
      }
    );
  };
  
  handleCalculate = (event) => {
    this.props.actions.calculate({ 
      calculate_params: {
        period_id: this.props.findActive.data.id,
        group_id: this.state.calculationFilters.filterGroup,
        polynomial_id: this.state.calculationFilters.filterPolynomial,
        polynomial_sub_model_id: this.state.calculationFilters.filterPolynomialSubModel,        
        calculation_units_criteria: this.state.calculationFilters.criteria,
        calculation_units: this.state.calculationFilters.calculationUnitsSelected,
        debug_mode: this.state.calculateForm.debug_mode
      } 
    });
  }; 

  handleRecalculateConfirmPopup = async (event) => {
    InterfaceTooling.handleShowPopup(
      event, popupIds.recalculateConfirmPopup, this.state, 
      this.setState.bind(this), this.props.calculate.loading, () => {
        var calculation = JSON.parse(event.currentTarget.dataset.entitydata);
        this.setState({
          isDebugCalculateForm: false,
          recalculationFilters: calculation.filters_object,
          calculateForm: JavascriptUtilsTooling.deepCopy(initialState.calculateForm)
        });
      }
    );
  };  

  handleRecalculate = (event) => {
    if(this.state.recalculationFilters) {
      this.state.recalculationFilters.recalculate = true
      this.state.recalculationFilters.debug_mode = this.state.calculateForm.debug_mode
    }
    this.props.actions.calculate({ 
      calculate_params: this.state.recalculationFilters
    });
  };

  handleCalculateForm = (event) => {
    let value = JavascriptUtilsTooling.getInputValueFromEvent(event);
    let dataPath = event.currentTarget.dataset.path;
    let newFormValues = JavascriptUtilsTooling.deepCopy(this.state.calculateForm);
    newFormValues[dataPath] = value;
    this.setState({
      calculateForm: newFormValues
    });
  };

  handleChangePeriodStateForm = (event) => {
    let value = JavascriptUtilsTooling.getInputValueFromEvent(event);
    let dataPath = event.currentTarget.dataset.path;
    let newFormValues = JavascriptUtilsTooling.deepCopy(this.state.reprocessForm);
    newFormValues[dataPath] = value;
    this.setState({
      reprocessForm: newFormValues
    });
  };

  handleChangePeriodStateConfirmPopup = async (event) => {
    InterfaceTooling.handleShowPopup(
      event, popupIds.changePeriodStateConfirmPopup, this.state, 
      this.setState.bind(this), this.props.changePeriodState.loading, () => {
        if(this.props.findActive.data.fix_polynomials) {
          let newFormValues = JavascriptUtilsTooling.deepCopy(this.state.reprocessForm);
          newFormValues.fix_polynomials = false;
          this.setState({
            reprocessForm: newFormValues
          });
        }
      }
    );
  };

  handleRevertReprocessConfirmPopup = async (event) => {
    InterfaceTooling.handleShowPopup(
      event, popupIds.rollBackPeriodConfirmPopup, this.state, 
      this.setState.bind(this), this.props.rollBackPeriod.loading
    );
  };

  handleRollBackPeriod = (event) => {
    this.props.actions.rollBackPeriod({ id: this.props.findActive.data.id });
  }
  
  handleChangePeriodState = (event) => {
    this.props.actions.changePeriodState({ 
      id: this.props.findActive.data.id,
      state: "reprocess",
      ...this.state.reprocessForm
    });
  }; 

  handleClosePeriodConfirmPopup = async (event) => {
    InterfaceTooling.handleShowPopup(
      event, popupIds.closePeriodConfirmPopup, this.state, 
      this.setState.bind(this), this.props.closePeriod.loading
    );
  };

  handleClosePeriod = (event) => {
    this.props.actions.closePeriod({ id: this.props.findActive.data.id });
  }; 

  handleRefreshMetricsLoadedData = (event) => {
    if(this.props.findActive.data && this.props.findActive.data.id && this.props.metricsGroupsWithDefaults.status==200) {
      this.getMetricsDataUploadInfoForAllMetrics(this.props.findActive.data.id);
    }
  };

  handleRefreshLoadDataInfoPopup = event => {
    this.props.actions.getProcessingMetricsImportsNotifications(this.props.findActive.data.id);
  };

  handleLoadDataInfoPopup = event => {
    InterfaceTooling.handleShowPopup(
      event, popupIds.loadDataInfoPopup, this.state, 
      this.setState.bind(this), this.props.processingMetricsImportsNotifications.loading, () => {
        this.props.actions.getProcessingMetricsImportsNotifications(this.props.findActive.data.id);
      }
    );
  };

  handleLoadDataInfoPopupNextPage = async (event) => {
    var page = +event.currentTarget.dataset.targetpage;
    if(this.props.processingMetricsImportsNotifications.status === 200 && 
       this.props.processingMetricsImportsNotifications.page<=this.props.processingMetricsImportsNotifications.totalPages) {
      await this.props.actions.getProcessingMetricsImportsNotifications(this.props.findActive.data.id, page);
    }  
  };
    
  handleReportFilesPopup = event => { 
    InterfaceTooling.handleShowPopup(
      event, popupIds.reportFilesPopup, this.state, 
      this.setState.bind(this), this.props.loadingReporFilesHistory, () => {
        this.props.reportFileActions.getReportFilesHistory({
          period_id: this.props.findActive.data.id,
          per_page: Constants.interface.popupPageSize,
          page: 1
        }); 
      }
    );
  };

  handleExportBiReportsPopup = event => { 
    InterfaceTooling.handleShowPopup(
      event, popupIds.exportBiReportsPopup, this.state, 
      this.setState.bind(this), this.props.exportBiReports.loading, () => {
        this.setState({
          exportBiReportsForm: {
            models_list: [],
            period_id: this.props.findActive.data.id
          }
        });
      }
    );
  };

  handleExportBiReports = event => {
    this.props.actions.exportBiReports(this.state.exportBiReportsForm);
  };

  handleOnChangeExportBiReportsFormData = (event) => {
    let value = JavascriptUtilsTooling.getInputValueFromEvent(event);
    let dataPath = event.currentTarget.dataset.path;
    let newFormValues = JavascriptUtilsTooling.deepCopy(this.state.exportBiReportsForm);
    if(dataPath=="template_id") {
      newFormValues.models_list[event.currentTarget.dataset.selectlist].template_id = value
    }
    else{
      let index = event.currentTarget.dataset.index;
      let listValue = event.currentTarget.value;
      let isDelete = event.currentTarget.dataset.delete;
      let models = JavascriptUtilsTooling.deepCopy(
        JavascriptUtilsTooling.getObjectStringPathKeyValue(this.state.exportBiReportsForm, dataPath)
      );            
      if(isDelete) { 
        delete models[index];
        models = models.filter(item => item);
      } else {
        models.push({model_id: listValue, template_id: "defect", name: (this.props.polynomialsList.data.find(item => item.id==listValue)).name});
      }
      newFormValues[dataPath] = JavascriptUtilsTooling.deepCopy(models)
    }
    this.setState({
      exportBiReportsForm: newFormValues
    });
  }

  handleOnChangeConnector = event => {
    let value = JavascriptUtilsTooling.getInputValueFromEvent(event);
    this.setState({
      connectorSelected: value,
    });
  };

  handleRefreshPage = async () => {
    await this.props.actions.getPeriodsList();
  }

  handleRefreshSpecificVirtualMetricData = async (event) => {
    var metricGroupSelected = event.currentTarget.dataset.entitydata ? JSON.parse(event.currentTarget.dataset.entitydata) : null;
    let metricsIdSelected = metricGroupSelected ? (metricGroupSelected.metric_ids ? metricGroupSelected.metric_ids.split(",") : []) : null
    if(metricGroupSelected){
      await this.props.actions.refreshVirtualMetricData({
        period_id: this.props.findActive.data.id,
        metrics_ids: metricsIdSelected,
        group_name: metricGroupSelected.name ? metricGroupSelected.name : "", 
      })
    }
  }

  handleRefreshVirtualMetricData = async (event) => {
    var pendantVirtualMetricData = event.currentTarget.dataset.entitydata ? JSON.parse(event.currentTarget.dataset.entitydata) : null;
    if(pendantVirtualMetricData){
      this.props.actions.refreshVirtualMetricData({
        period_id: this.props.findActive.data.id,
        metrics_ids: pendantVirtualMetricData["pendantVirtualMetricDataToLoadIds"],
        group_names: pendantVirtualMetricData["pendantVirtualMetricDataToLoadGroupNames"].join("', '")
      })
    }
  }

  render() {
    var loadingView =  (this.props.getCustomer.firstLoading || this.props.getCustomer.loading) || (this.props.periodsList.firstLoading || this.props.periodsList.loading) || ((this.props.polynomialsList.firstLoading || this.props.polynomialsSubModelList.firstLoading || this.props.importFilesList.firstLoading || (this.props.biTemplatesList.firstLoading || this.props.biTemplatesList.loading) ||
        this.props.metricsGroupsWithDefaults.loading || (this.props.groupsFilter.firstLoading || this.props.groupsFilter.loading)) && this.props.periodsList.status == 200 && this.props.periodsList.data.length>0);
    var errorLoadingView = (this.props.periodsList.status !== null && this.props.periodsList.status !== 200) ||
        (this.props.groupsFilter.status !== null && this.props.groupsFilter.status !== 200) ||
        (this.props.metricsGroupsWithDefaults.status !== null && this.props.metricsGroupsWithDefaults.status !== 200) ||
        (this.props.importFilesList.status !== null && this.props.importFilesList.status !== 200) ||
        (this.props.polynomialsList.status !== null && this.props.polynomialsList.status !== 200) ||
        (this.props.polynomialsSubModelList.status !== null && this.props.polynomialsSubModelList.status !== 200) ||
        (this.props.biTemplatesList.status !== null && this.props.biTemplatesList.status !== 200) ||
        (this.props.getCustomer.status !== null && this.props.getCustomer.status !== 200);
    let noPeriodsCreated = this.props.periodsList.status == 200 && this.props.periodsList.data.length==0;
    let processingPeriod = this.props.findActive.data.processing_closure || this.props.findActive.data.processing_rollback || this.props.findActive.data.processing_delete;
    let filterPolynomialOptionAll = [{id: Constants.selectKeys.periodAdmin.allModelsSelectKey, name: intl.formatMessage({ id: "periodAdmin.modelFilterAllOptionLabel" })}];
    
    //Set an array of pendant to load virtual metric's ids, and filter them by the selected polynomial filter
    let pendantVirtualMetricData = {};
    pendantVirtualMetricData["pendantVirtualMetricDataToLoadIds"] = [];
    pendantVirtualMetricData["pendantVirtualMetricDataToLoadGroupNames"] = [];
    let hideRefreshVirtualMetricButton = true;
    if(this.props.metricsGroupsWithDefaults && this.props.metricsDataUploadInfo && this.props.metricsGroupsWithDefaults.data.length>0){
      this.props.metricsGroupsWithDefaults.data.forEach((metricGroup, index)=> {
        if(metricGroup.metric_type == Constants.metricAdmin.metricType.VIRTUAL){
          let metricVersionsIds = metricGroup.metric_version_ids ? metricGroup.metric_version_ids.split(",") : []
          let metricIds = metricGroup.metric_ids ? metricGroup.metric_ids.split(",") : []
          //Filter by selected polynomial filter
          if((this.state.metricUploadFilters.selectedPolynomialIdModel==Constants.selectKeys.periodAdmin.allModelsSelectKey || this.state.metricUploadFilters.selectedPolynomialIdSubModel==Constants.selectKeys.periodAdmin.allModelsSelectKey) || 
          (this.props.polynomialFilterTreeInfo && this.props.polynomialFilterTreeInfo.data.polynomial_tree_info && 
          this.props.polynomialFilterTreeInfo.data.polynomial_tree_info.metric_versions_ids_used.some(used_metric_version_id => metricVersionsIds.includes(used_metric_version_id)))){
            let groupAdded = false;
            metricVersionsIds.forEach((versionId, versionIndex)=> {
              //Set if the refresh button should be shown (if there were any virtual metrics with current filter)
              hideRefreshVirtualMetricButton = false
              let findedInfo = (!this.props.metricsDataUploadInfo[index].loading && this.props.metricsDataUploadInfo[index].status==200) ? this.props.metricsDataUploadInfo[index].data.find(info => +info.metric_version_id == versionId) : null;
              if(findedInfo && findedInfo.virtual_metric_regenerate_pending){
                if(!groupAdded){
                  pendantVirtualMetricData["pendantVirtualMetricDataToLoadGroupNames"].push(metricGroup.name);
                }
                pendantVirtualMetricData["pendantVirtualMetricDataToLoadIds"].push(metricIds[versionIndex]);
                groupAdded = true
              }
            });
          }
        }
      });
    }     
    return (
      <section className="dashboard_content_inner_view entity_admin_container period_admin_container">
        {(!loadingView) && (  
          <i 
            className={"fas fa-sync top_refresh_button"}
            onClick={this.handleRefreshPage}
          />
        )}
        {(loadingView) ? (<FullScreenGlassLoading/>) : 
          noPeriodsCreated ? (
            <section className={"period_admin_no_period_wrapper"}>
              <div className="loaded_period_admin_wrapper">
                <Header/>
                <div className="no_period_selected_wrapper">
                  {intl.formatMessage({ id: "common.noPeriodCreated" })}
                </div>
              </div>
            </section>            
          ) :
          errorLoadingView ? (<ErrorLoadingView/>) :
          (
            <section>
                {(!this.props.findActive.data.id) && (
                  <div>
                    <Header/>
                    <div className="period_processing_closing_wrapper">
                      <FormattedMessage id={"periodAdmin.periodNotFounded"}/>
                    </div>
                  </div>
                )}
              {(this.props.findActive.data.id && processingPeriod) && (
                <section className={"left_filter_fixed_section_parent" + (this.state.calculationsCollapse ? " left_filter_fixed_section_parent_collapse" : "")}>
                  <div className="left_filter_fixed_section">
                    <Select
                      className="period_admin_filters_wrapper_period_selector"
                      value={this.props.findActive.data.id}
                      onChange={this.handleOnChangePeriod}
                      labelLocale={<FormattedMessage id="common.periodSelectLabel"/>}
                      options={InterfaceTooling.getSelectArrayFromArrayOfObjects(this.props.periodsList.data, "id", "name", false)}
                    />
                    <div className="left_filter_fixed_section_collapse_component" onClick={this.handleCalculationsCollapse}>
                      <i className={"fas "+ (this.state.calculationsCollapse ? "fa-angle-right" : "fa-angle-left")}/>
                    </div>
                  </div>
                  <div className="left_filter_right_section loaded_period_admin_wrapper">
                    <Header/>
                    <div className="period_processing_closing_wrapper">
                      {(this.props.findActive.data.processing_closure) ? (
                        <React.Fragment>
                          <FormattedMessage id={"periodAdmin.processingPeriodClausureMessage"}/>{ " '" + this.props.findActive.data.name + "'"}
                        </React.Fragment>
                      ) : (this.props.findActive.data.processing_rollback) ? (
                        <React.Fragment>
                          <FormattedMessage id={"periodAdmin.processingPeriodRollbackMessage"}/>{ " '" + this.props.findActive.data.name + "'"}
                        </React.Fragment>
                      ) : (this.props.findActive.data.processing_delete) ? (
                        <React.Fragment>
                          <FormattedMessage id={"periodAdmin.processingPeriodDeleteMessage"}/>{ " '" + this.props.findActive.data.name + "'"}
                        </React.Fragment>
                      ) : (null)}
                    </div>
                  </div>
                </section>   
                )}
                {(this.props.findActive.data.id && !processingPeriod) && (
                  <section className={"left_filter_fixed_section_parent" + (this.state.calculationsCollapse ? " left_filter_fixed_section_parent_collapse" : "")}>
                    <div className="left_filter_fixed_section">
                      <Select
                        className="period_admin_filters_wrapper_period_selector"
                        disabled={this.props.fixedPeriodId!=""}
                        value={this.props.findActive.data.id}
                        onChange={this.handleOnChangePeriod}
                        labelLocale={<FormattedMessage id="common.periodSelectLabel"/>}
                        options={InterfaceTooling.getSelectArrayFromArrayOfObjects(this.props.periodsList.data, "id", "name", false)}
                      />
                      <CalculationsHeader/>
                      <CalculationUnitsFilter
                        onChangeFilter={this.handleOnChangeCalculationFilters}
                        filterValues={this.state.calculationFilters}
                        groupsData={this.props.groupsFilter.data}
                        modelsData={this.props.polynomialsList.data}
                        subModelsData={this.props.polynomialsSubModelList.data}                        
                        calculationUnitsData={this.props.calculationUnitsFilter.data}
                        calculationUnitsLoading={this.props.calculationUnitsFilter.loading}
                        calculationUnitsFetchAction={this.handleFetchFilterCalculationUnits}
                      />
                      <CalculateButton
                        loadingCalculate={this.props.calculate.loading}
                        handleCalculate={this.handleCalculateConfirmPopup}
                        handleShowHistory={this.handleCalculationsPopup}
                        handleExportBiReports={this.handleExportBiReportsPopup}
                        handleClickMenuItem={this.handleClickMenuItem}
                        handleReportFilesPopup={this.handleReportFilesPopup}
                        calculateDisabled={this.state.calculationFilters.criteria=="" || 
                          (this.state.calculationFilters.criteria==Constants.calculationFilters.calculationUnitFilter.ONLY &&
                          this.state.calculationFilters.calculationUnitsSelected.length==0)
                        }
                        periodId={this.props.findActive.data.id}
                      />
                      {(this.props.findActive.data.state!="reprocess") ? (
                        <ChangePeriodStateButton
                          loadingChangePeriodState={this.props.changePeriodState.loading}
                          handleChangePeriodState={this.handleChangePeriodStateConfirmPopup}
                        />
                      ) : (
                        <ChangePeriodStateButton
                          loadingChangePeriodState={this.props.changePeriodState.loading}
                          handleChangePeriodState={this.handleRevertReprocessConfirmPopup}
                          buttonLabelText={<FormattedMessage id="periodAdmin.revertReprocessStateLabel"/>}
                        />
                      )}
                      <ClosePeriodButton
                        loadingClosePeriod={this.props.closePeriod.loading}
                        handleClosePeriod={this.handleClosePeriodConfirmPopup}
                      />
                      <div className="left_filter_fixed_section_collapse_component" onClick={this.handleCalculationsCollapse}>
                        <i className={"fas "+ (this.state.calculationsCollapse ? "fa-angle-right" : "fa-angle-left")}/>
                      </div>
                    </div>
                    <div className="left_filter_right_section loaded_period_admin_wrapper">
                      <Header
                        periodState={this.props.findActive.data.state}
                      />
                      <PeriodInfo
                        periodData={this.props.findActive.data}
                        handleEditPeriod={EntityTableTooling.handleShowFormPopup.bind(null, this)}
                      />
                      <CalculationsUnitsHeader />
                      <CalculationsUnitsLoadData
                        handleClickMenuItem={this.handleClickMenuItem}
                        periodId={this.props.findActive.data.id}
                      />
                      <LoadDataHeader
                        handleRefresh={this.handleRefreshMetricsLoadedData}
                        handleInfo={this.handleLoadDataInfoPopup}
                        onChangePolynomialFilter={this.handlePolynomialFilterSelection}
                        selectedPolynomialIdModel={this.state.metricUploadFilters.selectedPolynomialIdModel}
                        selectedPolynomialIdSubModel={this.state.metricUploadFilters.selectedPolynomialIdSubModel}
                        filterPolynomialOptions={InterfaceTooling.getSelectArrayFromArrayOfObjects(filterPolynomialOptionAll.concat(this.props.polynomialsList.data), "id", "name", false)}
                        filterPolynomialSubModelOptions={InterfaceTooling.getSelectArrayFromArrayOfObjects(filterPolynomialOptionAll.concat(this.props.polynomialsSubModelList.data), "id", "name", false)}
                        polynomialFilterTreeInfoLoading={this.props.polynomialFilterTreeInfo.loading}
                        hideRefreshButton={hideRefreshVirtualMetricButton}
                        handleRefreshVirtualMetricData={this.handleRefreshVirtualMetricData}
                        pendantVirtualMetricData={pendantVirtualMetricData}
                      />
                      {(!this.props.polynomialFilterTreeInfo.loading && 
                          (this.props.polynomialFilterTreeInfo.status == 200 || 
                           this.state.metricUploadFilters.selectedPolynomialIdToSend=="" || 
                           this.state.metricUploadFilters.selectedPolynomialIdToSend=="all")
                       ) && (
                        <React.Fragment>
                          <MetricGroupsLoadData
                            metricGroups={this.props.metricsGroupsWithDefaults.data}
                            handleMetricGroupClick={this.handleShowImportFormPopup}
                            metricsDataUploadInfoArray={this.props.metricsDataUploadInfo}
                            selectedPolynomialId={this.state.metricUploadFilters.selectedPolynomialIdToSend}
                            polynomialFilterTreeInfoData={this.props.polynomialFilterTreeInfo.data}
                            handleRefreshVirtualMetricData={this.handleRefreshSpecificVirtualMetricData}
                            isPeriodLoadData={true}
                          />
                          <MetricGroupsLoadData
                            metricGroups={this.props.metricsGroupsWithDefaults.data}
                            handleMetricGroupClick={this.handleShowImportFormPopup}
                            metricsDataUploadInfoArray={this.props.metricsDataUploadInfo}
                            selectedPolynomialId={this.state.metricUploadFilters.selectedPolynomialIdToSend}
                            polynomialFilterTreeInfoData={this.props.polynomialFilterTreeInfo.data}
                            handleRefreshVirtualMetricData={this.handleRefreshSpecificVirtualMetricData}
                            isPeriodLoadData={false}
                          />
                        </React.Fragment>
                      )}
                    </div>
                  </section>
                )}
                <FormPopup
                  handleShowPopup={EntityTableTooling.handleShowFormPopupWithConfirmOnExit.bind(null, this)}
                  handleOnChangeForm={EntityTableTooling.handleOnChangeForm.bind(null, this)}
                  handleButton={EntityTableTooling.handleOnSaveForm.bind(null, this)}
                  loadingButton={this.props.save.loading}
                  fullLoading={this.props.save.loading}
                  statusButton={this.props.save.status}
                  formData={this.props.save.data}
                  errors={this.props.save.errors}
                  entityNameSingular={<FormattedMessage id="periodAdmin.entityNameSingular"/>}
                  popupState={EntityTableTooling.getFormPopupState(this)}
                />                
                <ImportMetricDataFormPopup
                  handleShowPopup={this.handleShowImportFormPopup}
                  handleDownloadImportSheet={this.handleDownloadImportSheet}
                  handleButton={this.handleOnSaveImportForm}
                  loadingButton={this.props.import.loading}
                  fullLoading={this.props.import.loading || this.props.downloadImport.loading}
                  statusButton={this.props.import.status}
                  errors={this.props.import.errors}
                  popupState={this.state[popupIds.importFormPopup]}
                  importFilesList={this.props.importFilesList.data}
                  selectedImportFile={this.state.selectedImportFile}
                  handleOnChangeImportFile={this.handleOnChangeImportFile}
                  metricGroupSelected={this.state.metricGroupSelected}
                />
                <CalculationsPopup
                  handleShowPopup={this.handleCalculationsPopup}
                  handleRefresh={this.handleRefreshCalculations}
                  fullLoading={this.props.calculationsHistory.firstLoading || this.props.downloadCalculationReport.loading || this.props.downloadCalculationUnifiedReport.loading}
                  tableLoading={this.props.calculationsHistory.loading}
                  calculationsState={this.props.calculationsHistory}
                  handleCalculationsNextPage={this.handleCalculationsPopupNextPage}
                  handleCalculationsDelete={this.handleCalculationDeletePopup}
                  handleDownloadCalculationReport={this.handleDownloadCalculationReport}
                  handleDownloadCalculationReportCustom={this.handleDownloadCalculationReportCustom}
                  handleDownloadCalculationUnifiedReport={this.handleDownloadCalculationUnifiedReport}
                  handleDownloadCalculationUnifiedReportCustom={this.handleDownloadCalculationUnifiedReportCustom}
                  handleDownloadCalculationUnifiedReportReprocess={this.handleDownloadCalculationUnifiedReportReprocess}
                  handleDownloadCalculationUnifiedReportCustomReprocess={this.handleDownloadCalculationUnifiedReportCustomReprocess}
                  handleRecalculate={this.handleRecalculateConfirmPopup}
                  handleJobInfo={this.props.handleNotificationDetailPopupReference}
                  hasReprocess={this.props.findActive.data.reprocess_at}
                  popupState={this.state[popupIds.calculationsPopup]}
                  customerConnectors={this.props.getCustomer.data.connectors}
                  handleSelectConnectors={this.handleOnChangeConnector}
                  currentConnector={this.state.connectorSelected}
                />
                <ExportBiReportsFormPopup
                  handleShowPopup={this.handleExportBiReportsPopup}
                  handleButton={this.handleExportBiReports}
                  loadingButton={this.props.exportBiReports.loading}
                  fullLoading={this.props.exportBiReports.loading}
                  statusButton={this.props.exportBiReports.status}
                  formData={this.state.exportBiReportsForm}
                  errors={this.props.exportBiReports.errors}
                  popupState={this.state[popupIds.exportBiReportsPopup]}
                  modelsData={this.props.polynomialsList.data}
                  biTemplatesData={this.props.biTemplatesList.data}
                  handleOnChangeFormData={this.handleOnChangeExportBiReportsFormData}
                />
                <LoadDataInfoPopup
                  handleShowPopup={this.handleLoadDataInfoPopup}
                  handleRefresh={this.handleRefreshLoadDataInfoPopup}
                  fullLoading={this.props.processingMetricsImportsNotifications.firstLoading}
                  tableLoading={this.props.processingMetricsImportsNotifications.loading}
                  listState={this.props.processingMetricsImportsNotifications}
                  handleNextPage={this.handleLoadDataInfoPopupNextPage}
                  handleRowClick={this.props.handleNotificationDetailPopupReference}
                  popupState={this.state[popupIds.loadDataInfoPopup]}
                />                
                <GlassConfirmPopup
                  confirmTitle={(<FormattedMessage 
                   id={"common.entity.deleteEntityConfirmTitle"}
                   values={{entityNameSingular: (<FormattedMessage id={"calculations.entityNameSingular"}/>)}}
                  />)}
                  confirmText={(<div>
                    <FormattedMessage 
                     id={"common.entity.deleteEntityConfirm"}
                     values={{entityNameSingular: (<FormattedMessage id={"calculations.entityNameSingularWithArticle"}/>)}}
                    />
                    <div>{this.state.deleteCalculationTextInfo}</div>
                  </div>)}
                  handleConfirm={this.handleCalculationDeleteConfirm}
                  handleCancel={this.handleCalculationDeletePopup}
                  popupState={this.state[popupIds.calculationDeletePopup]}
                  loading={this.props.deleteCalculation.loading}
                />
                <GlassConfirmPopup
                  confirmTitle={(<FormattedMessage id={"calculations.calculatePopupTitle"}/>)}
                  confirmText={(<FormattedMessage id={"calculations.calculatePopupDetail"}/>)}
                  handleConfirm={this.handleCalculate}
                  handleCancel={this.handleCalculateConfirmPopup}
                  popupState={this.state[popupIds.calculateConfirmPopup]}
                  loading={this.props.calculate.loading}
                >
                  <CalculateForm
                    isDebugMode={this.state.isDebugCalculateForm}
                    handleOnChangeForm={this.handleCalculateForm}
                    formData={this.state.calculateForm}
                  />          
                </GlassConfirmPopup>
                <GlassConfirmPopup
                  confirmTitle={(<FormattedMessage id={"calculations.recalculatePopupTitle"}/>)}
                  confirmText={(LocaleTooling.getCalculationUnitContextLocale(<FormattedMessage id={"calculations.recalculatePopupDetail"}/>))}
                  handleConfirm={this.handleRecalculate}
                  handleCancel={this.handleRecalculateConfirmPopup}
                  popupState={this.state[popupIds.recalculateConfirmPopup]}
                  loading={this.props.calculate.loading}
                >
                  <CalculateForm
                    isDebugMode={this.state.isDebugCalculateForm}
                    handleOnChangeForm={this.handleCalculateForm}
                    formData={this.state.calculateForm}
                  />
                </GlassConfirmPopup>
                <GlassConfirmPopup
                  confirmTitle={(<FormattedMessage id={"calculations.changePeriodStatePopupTitle"}/>)}
                  confirmText={(<div>
                    <FormattedMessage id={"calculations.changePeriodStatePopupDetail"}/>
                    <ReprocessForm
                      handleOnChangeForm={this.handleChangePeriodStateForm}
                      formData={this.state.reprocessForm}
                      hideFixPolynomials={this.props.findActive.data.fix_polynomials && !this.state.reprocessForm.fix_polynomials}
                    />
                  </div>)}
                  handleConfirm={this.handleChangePeriodState}
                  handleCancel={this.handleChangePeriodStateConfirmPopup}
                  popupState={this.state[popupIds.changePeriodStateConfirmPopup]}
                  loading={this.props.changePeriodState.loading}
                />
                <GlassConfirmPopup
                  confirmTitle={(<FormattedMessage id={"calculations.closePeriodPopupTitle"}/>)}
                  confirmText={(<FormattedMessage id={"calculations.closePeriodPopupDetail"}/>)}
                  handleConfirm={this.handleClosePeriod}
                  handleCancel={this.handleClosePeriodConfirmPopup}
                  popupState={this.state[popupIds.closePeriodConfirmPopup]}
                  loading={this.props.closePeriod.loading}
                />
                <GlassConfirmPopup
                  confirmTitle={(<FormattedMessage id={"periodAdmin.rollBackPeriodConfirmTitle"}/>)}
                  confirmText={(<FormattedMessage id={"periodAdmin.rollBackPeriodConfirmText"}/>)}
                  handleConfirm={this.handleRollBackPeriod}
                  handleCancel={this.handleRevertReprocessConfirmPopup}
                  popupState={this.state[popupIds.rollBackPeriodConfirmPopup]}
                  loading={this.props.rollBackPeriod.loading}
                />
                <ReportFilesPopupContainer
                  popupState={this.state[popupIds.reportFilesPopup]}
                  handleRepotFilesPopup={this.handleReportFilesPopup}
                  periodData={this.props.findActive.data}
                  periodId={this.props.findActive.data.id}
                  hasReprocess={this.props.findActive.data.reprocess_at}
                  handleShowEmailDetailPopup={this.handleShowEmailDetailPopup}
                />
                <GlassConfirmPopup
                  confirmTitle={(<FormattedMessage id={"common.confirmExitPopupTitle"}/>)}
                  confirmText={(<FormattedMessage id={"common.confirmExitPopupDetail"}/>)}
                  handleConfirm={InterfaceTooling.handleCloseForm.bind(null, this)}
                  handleCancel={InterfaceTooling.handleCloseFormConfirmPopup.bind(null, this)}
                  popupState={InterfaceTooling.getCloseFormConfirmPopupState(this)}
                  loading={false}
                />
            </section>
          )
        }
      </section>
    );
  }
}

const mapStateToProps = state => ({
  periodsList: state.reducer.periodAdmin.periodsList,
  save: state.reducer.periodAdmin.save,
  findActive: state.reducer.periodAdmin.findActive,
  changePeriodState: state.reducer.periodAdmin.changePeriodState,
  closePeriod: state.reducer.periodAdmin.closePeriod,
  groupsFilter: state.reducer.periodAdmin.groupsFilter,
  calculationUnitsFilter: state.reducer.periodAdmin.calculationUnitsFilter,
  metricsGroupsWithDefaults: state.reducer.periodAdmin.metricsGroupsWithDefaults,
  import: state.reducer.periodAdmin.import,
  downloadImport: state.reducer.periodAdmin.downloadImport,
  importFilesList: state.reducer.periodAdmin.importFilesList,
  calculate: state.reducer.periodAdmin.calculate,
  calculationsHistory: state.reducer.periodAdmin.calculationsHistory,
  deleteCalculation: state.reducer.periodAdmin.deleteCalculation,
  downloadCalculationReport: state.reducer.periodAdmin.downloadCalculationReport,
  downloadCalculationUnifiedReport: state.reducer.periodAdmin.downloadCalculationUnifiedReport,
  polynomialsList: state.reducer.periodAdmin.polynomialsList,
  polynomialsSubModelList: state.reducer.periodAdmin.polynomialsSubModelList,
  metricsDataUploadInfo: state.reducer.periodAdmin.metricsDataUploadInfo,
  biTemplatesList: state.reducer.periodAdmin.biTemplatesList,
  exportBiReports: state.reducer.periodAdmin.exportBiReports,
  processingMetricsImportsNotifications: state.reducer.periodAdmin.processingMetricsImportsNotifications, 
  polynomialFilterTreeInfo: state.reducer.periodAdmin.polynomialFilterTreeInfo,
  handleNotificationDetailPopupReference: state.reducer.dashboard.handleNotificationDetailPopupReference,
  loadingReporFilesHistory: state.reducer.reportFileAdmin.reportFilesHistory.loading,
  fixedPeriodId: state.reducer.dashboard.fixedPeriodId,
  getCustomer: state.reducer.periodAdmin.getCustomer,
  rollBackPeriod: state.reducer.periodAdmin.rollBackPeriod,
  refreshVirtualMetricData: state.reducer.periodAdmin.refreshVirtualMetricData,
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(periodAdminActions, dispatch),
  dashboardActions: bindActionCreators(dashboardActions, dispatch),
  reportFileActions: bindActionCreators(reportFileActions, dispatch)
});

PeriodAdmin.propTypes = {};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PeriodAdmin);



// WEBPACK FOOTER //
// ./source/client/containers/periodAdmin/index.jsx